import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { inchargePinAPI } from '../utils/api';
import '../styles/Navbar.css';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);

  // PIN modal state
  const [pinModal, setPinModal] = useState(null); // 'akshay' | 'canteen' | null
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  // ── PIN helpers ─────────────────────────────────────────────────────────────
  const openPinModal = (type) => {
    setPinModal(type);
    setPinValue('');
    setPinError('');
  };

  const closePinModal = () => {
    setPinModal(null);
    setPinValue('');
    setPinError('');
  };

  const handlePinInput = (digit) => {
    if (pinValue.length >= 6) return;
    const next = pinValue + digit;
    setPinValue(next);
    setPinError('');
    if (next.length === 6) {
      setTimeout(() => verifyPin(next), 150);
    }
  };

  const handlePinBackspace = () => {
    setPinValue(v => v.slice(0, -1));
    setPinError('');
  };

  const verifyPin = async (entered) => {
    setPinLoading(true);
    try {
      await inchargePinAPI.verify(pinModal, entered);
      // Store PIN in sessionStorage so API calls can send it in headers
      const sessionKey = pinModal === 'canteen' ? 'vn_canteen_pin_session' : 'vn_akshay_pin_session';
      sessionStorage.setItem(sessionKey, entered);
      const dest = pinModal === 'akshay' ? '/akshay-incharge' : '/canteen-incharge';
      closePinModal();
      setDrawerOpen(false);
      navigate(dest);
    } catch (err) {
      setPinError(err.message || 'Incorrect PIN. Please try again.');
      setPinValue('');
    } finally {
      setPinLoading(false);
    }
  };
  // ───────────────────────────────────────────────────────────────────────────

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
    setDrawerOpen(false);
    setProfileOpen(false);
  };

  const navigateTo = (path) => {
    navigate(path);
    setMenuOpen(false);
    setDrawerOpen(false);
  };

  return (
    <>
      {/* Side Drawer Overlay */}
      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Side Drawer */}
      <div className={`side-drawer ${drawerOpen ? 'side-drawer--open' : ''}`}>
        <div className="side-drawer__header">
          <span className="side-drawer__title">Menu</span>
          <button className="side-drawer__close" onClick={() => setDrawerOpen(false)}>✕</button>
        </div>

        {/* Profile card – visible only when logged in */}
        {token && user && (
          <div className="side-drawer__profile">
            <div className="side-drawer__profile-avatar">
              {user.profilePicture ? (
                <img src={user.profilePicture} alt={user.name} />
              ) : (
                <span className="side-drawer__profile-initials">
                  {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                </span>
              )}
            </div>
            <div className="side-drawer__profile-info">
              <p className="side-drawer__profile-name">{user.name}</p>
              <span className={`side-drawer__profile-role side-drawer__profile-role--${user.role}`}>
                {user.role === 'admin' ? '🔒 Admin' : user.role === 'teacher' ? '🎓 Teacher' : '📘 Student'}
              </span>
            </div>
          </div>
        )}
        <nav className="side-drawer__nav">
          <button onClick={() => navigateTo('/')}>🏠 Home</button>
          <button onClick={() => navigateTo('/our-courses')}>📚 Features</button>
          <button onClick={() => navigateTo('/store')}>⭐ AKSHAY</button>
          <button onClick={() => navigateTo('/canteen')}>🍽️ Canteen</button>
          <button onClick={() => navigateTo('/notices')}>📌 Notices</button>
          <button onClick={() => navigateTo('/gallery')}>🖼️ Gallery</button>
          <button onClick={() => navigateTo('/about-us')}>ℹ️ About Us</button>
          <button onClick={() => navigateTo('/contact-us')}>📞 Contact Us</button>

          {/* ── Staff Portal ─────────────────────────────────── */}
          <div className="staff-portal-section">
            <button
              className="staff-portal-toggle"
              onClick={() => setStaffOpen(o => !o)}
            >
              <span>🏢 Staff Portal</span>
              <span className={`staff-portal-arrow ${staffOpen ? 'staff-portal-arrow--open' : ''}`}>▾</span>
            </button>
            {staffOpen && (
              <div className="staff-portal-dropdown">
                <button className="staff-portal-item" onClick={() => openPinModal('akshay')}>
                  🏬 AKSHAY
                </button>
                <button className="staff-portal-item" onClick={() => openPinModal('canteen')}>
                  🍽️ CANTEEN
                </button>
              </div>
            )}
          </div>
          {/* ──────────────────────────────────────────────────── */}

          {token && user ? (
            <>
              <button onClick={() => navigateTo('/classes')}>📚 Classes</button>
              {user.role === 'student' && (
                <button onClick={() => navigateTo('/student-dashboard')}>📊 Dashboard</button>
              )}
              {user.role === 'teacher' && (
                <button onClick={() => navigateTo('/teacher-dashboard')}>📊 Dashboard</button>
              )}
              {user.role === 'admin' && (
                <button onClick={() => navigateTo('/admin-dashboard')}>🔒 Admin</button>
              )}
              <button className="side-drawer__logout" onClick={handleLogout}>🚪 Logout</button>
            </>
          ) : (
            <>
              <button onClick={() => navigateTo('/login')}>🔑 Login</button>
              <button onClick={() => navigateTo('/signup')}>✍️ Sign Up</button>
            </>
          )}
        </nav>
      </div>

      <nav className="navbar">
        <div className="nav-container">
          {/* Left hamburger + logo group */}
          <div className="nav-left-group">
            <button
              className="left-hamburger"
              onClick={() => setDrawerOpen(o => !o)}
              aria-label="Open menu"
            >
              <span /><span /><span />
            </button>
            <div className="logo" onClick={() => navigateTo('/')}>
              <img src="/images/logo.png" alt="Vidya Niketan Logo" className="logo-img" />
              <span className="logo-text" style={{ color: 'orange' }}>Vidya Niketan</span>
            </div>
          </div>

        <div className={`nav-menu ${menuOpen ? 'active' : ''}`}>
          <button className="nav-link" onClick={() => navigateTo('/')} style={{ color: 'orange', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Home</button>
          <button className="nav-link" onClick={() => navigateTo('/our-courses')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Features</button>
          <button className="nav-link" onClick={() => navigateTo('/store')} style={{ color: 'orange', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>AKSHAY</button>
          <button className="nav-link" onClick={() => navigateTo('/canteen')} style={{ color: '#fbbf24', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Canteen</button>
          <button className="nav-link" onClick={() => navigateTo('/notices')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Notices</button>
          <button className="nav-link" onClick={() => navigateTo('/gallery')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Gallery</button>
          
          {token && user ? (
            <>
              <span className="nav-user">Welcome, {user.name}</span>
              {user.role === 'student' && (
                <button className="nav-btn" onClick={() => navigateTo('/student-dashboard')}>
                  📊 Dashboard
                </button>
              )}
              {user.role === 'teacher' && (
                <button className="nav-btn" onClick={() => navigateTo('/teacher-dashboard')}>
                  📊 Dashboard
                </button>
              )}
              {user.role === 'admin' && (
                <button className="nav-btn" onClick={() => navigateTo('/admin-dashboard')}>
                  🔒 Admin
                </button>
              )}
              {/* Round profile avatar with dropdown */}
              <div className="nav-profile" ref={profileRef}>
                <button
                  className="nav-profile__btn"
                  onClick={() => setProfileOpen(o => !o)}
                  aria-label="Profile menu"
                >
                  {user.profilePicture ? (
                    <img src={user.profilePicture} alt={user.name} className="nav-profile__img" />
                  ) : (
                    <span className="nav-profile__initials">
                      {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                    </span>
                  )}
                </button>
                {profileOpen && (
                  <div className="nav-profile__dropdown">
                    <p className="nav-profile__dropdown-name">{user.name}</p>
                    <p className="nav-profile__dropdown-role">
                      {user.role === 'admin' ? '🔒 Admin' : user.role === 'teacher' ? '🎓 Teacher' : '📘 Student'}
                    </p>
                    <hr className="nav-profile__dropdown-divider" />
                    <button className="nav-profile__logout" onClick={handleLogout}>
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button className="nav-btn" onClick={() => navigateTo('/login')}>
                Login
              </button>
              <button className="nav-btn primary-btn" onClick={() => navigateTo('/signup')}>
                Sign Up
              </button>
            </>
          )}
        </div>

        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>

      {/* ── PIN Modal ─────────────────────────────────────────────────────── */}
      {pinModal && (
        <div className="pin-modal-overlay" onClick={closePinModal}>
          <div className="pin-modal" onClick={e => e.stopPropagation()}>
            <button className="pin-modal__close" onClick={closePinModal}>✕</button>
            <div className="pin-modal__icon">
              {pinModal === 'akshay' ? '🏬' : '🍽️'}
            </div>
            <h2 className="pin-modal__title">
              {pinModal === 'akshay' ? 'AKSHAY Incharge' : 'Canteen Incharge'}
            </h2>
            <p className="pin-modal__subtitle">Enter 6-digit PIN to continue</p>

            {/* PIN dots display */}
            <div className="pin-modal__dots">
              {[0,1,2,3,4,5].map(i => (
                <span
                  key={i}
                  className={`pin-modal__dot ${i < pinValue.length ? 'pin-modal__dot--filled' : ''}`}
                />
              ))}
            </div>

            {pinError && <p className="pin-modal__error">{pinError}</p>}

            {/* Numpad */}
            <div className="pin-modal__numpad">
              {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((key, idx) => (
                <button
                  key={idx}
                  className={`pin-modal__key ${key === '' ? 'pin-modal__key--empty' : ''} ${key === '⌫' ? 'pin-modal__key--back' : ''}`}
                  onClick={() => {
                    if (key === '⌫') handlePinBackspace();
                    else if (key !== '') handlePinInput(key);
                  }}
                  disabled={key === '' || pinLoading}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* ─────────────────────────────────────────────────────────────────── */}
    </>
  );
};

export default Navbar;
